import React, { useState, useCallback, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Badge, Nav, Spinner, Table, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faPaperPlane, faTerminal, faBook, faCloud, faServer, faLock, faLockOpen, faDatabase } from '@fortawesome/free-solid-svg-icons';
import { getConfig } from '../../config/getConfig';

// ─── Animations ───

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.5}`;

// ─── Styled Components ───

const PageWrapper = styled.div`animation: ${fadeIn} .3s ease;`;

const UrlBarWrapper = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; background: #fff;
  border: 2px solid #e2e8f0; border-radius: 10px;
  transition: border-color .15s, box-shadow .15s;
  &:focus-within { border-color: #ff6c37; box-shadow: 0 0 0 3px rgba(255,108,55,.08); }
`;

const MethodBadge = styled.span`
  background: #49cc90; color: #fff; font-weight: 700;
  font-size: .78rem; padding: 6px 14px; border-radius: 6px;
  white-space: nowrap; letter-spacing: .5px;
`;

const CodeArea = styled.textarea`
  width: 100%; min-height: ${p => p.$h || '280px'};
  font-family: 'SFMono-Regular',Consolas,'Liberation Mono',Menlo,monospace;
  font-size: 12.5px; line-height: 1.6; padding: 14px;
  border: 1px solid #e2e8f0; border-radius: 8px;
  background: #1e1e2e; color: #cdd6f4; resize: vertical; tab-size: 2;
  &:focus { outline: none; border-color: #ff6c37; box-shadow: 0 0 0 3px rgba(255,108,55,.1); }
`;

const ResponsePre = styled.pre`
  font-family: 'SFMono-Regular',Consolas,'Liberation Mono',Menlo,monospace;
  font-size: 12.5px; line-height: 1.6; padding: 14px;
  background: #1e1e2e; border-radius: 8px;
  max-height: 400px; overflow: auto;
  white-space: pre-wrap; word-break: break-word; margin: 0;
  color: ${p => p.$error ? '#f38ba8' : '#a6e3a1'};
  animation: ${fadeIn} .3s ease;
`;

const StatusBar = styled.div`
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  padding: 10px 14px; border-radius: 8px; margin-bottom: 10px;
  font-size: .85rem; animation: ${fadeIn} .2s ease;
  background: ${p => p.$error ? '#fff5f5' : '#f0fff4'};
  border: 1px solid ${p => p.$error ? '#feb2b2' : '#c6f6d5'};
`;

const DocScroll = styled.div`
  max-height: calc(100vh - 220px); overflow-y: auto;
  font-size: .84rem; padding-right: 4px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 4px; }
`;

const CodeBlock = styled.pre`
  font-family: 'SFMono-Regular',Consolas,'Liberation Mono',Menlo,monospace;
  font-size: 12px; line-height: 1.5; padding: 12px;
  background: #2d2d2d; color: #f8f8f2; border-radius: 6px;
  overflow-x: auto; white-space: pre-wrap; word-break: break-word; margin: 0;
`;

const HeaderRow = styled.div`display:flex;gap:8px;align-items:center;margin-bottom:6px;`;

const CurlBox = styled.div`
  position: relative; margin-top: 12px; padding: 12px 14px;
  background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px;
`;

const CurlPre = styled.pre`
  font-family: 'SFMono-Regular',Consolas,'Liberation Mono',Menlo,monospace;
  font-size: 11.5px; line-height: 1.5; color: #4a5568;
  margin: 0; white-space: pre-wrap; word-break: break-all;
`;

const CopyBtn = styled.button`
  position: absolute; top: 8px; right: 8px;
  background: ${p => p.$copied ? '#48bb78' : '#edf2f7'}; color: ${p => p.$copied ? '#fff' : '#718096'};
  border: none; border-radius: 6px; padding: 4px 10px;
  font-size: .75rem; cursor: pointer; transition: all .15s;
  display: flex; align-items: center; gap: 4px;
  &:hover { background: ${p => p.$copied ? '#48bb78' : '#e2e8f0'}; }
`;

const SectionLabel = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-weight: 600; font-size: .9rem; margin-bottom: 8px;
`;

const EmptyResponse = styled.div`
  text-align: center; padding: 48px 20px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-radius: 10px; border: 2px dashed #cbd5e0;
  color: #a0aec0;
`;

const LoadingDots = styled.span`
  &::after {
    content: '...';
    animation: ${pulse} 1.5s infinite;
  }
`;

const TabButton = styled.button`
  font-size: .82rem; padding: 6px 18px; font-weight: 600;
  border: none; border-radius: 6px; cursor: pointer;
  transition: all .15s;
  background: ${p => p.$active ? '#fff' : 'rgba(255,255,255,.2)'};
  color: ${p => p.$active ? '#ff6c37' : 'rgba(255,255,255,.9)'};
  &:hover { background: ${p => p.$active ? '#fff' : 'rgba(255,255,255,.35)'}; }
`;

// ─── Copy Hook ───

const useCopy = () => {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);
  return [copied, copy];
};

// ─── Example Data ───

const CF_ENCRYPT = {
  data: {
    "0": "This is a test string.", "1": "Confidential data 12345.",
    "2": "Password: P@ssw0rd!", "3": "User's email: user@example.com",
    "4": "Sensitive transaction details.", "5": "Personal Identification Number: 9876",
    "6": "Credit Card Number: 4111 1111 1111 1111", "7": "API Key: AKIAIOSFODNN7EXAMPLE",
    "8": "Token: 9d8f7e6b-4c3a-2f1e-b5c7-8a9b1c2d3e4f",
    "9": "Location Coordinates: 37.7749\u00b0 N, 122.4194\u00b0 W"
  },
  uuid: "c14517e1-a581-40e2-9251-9a1045ad6570",
  apikey: "iW1-9aibtEBRYgV80Ye3OsEki9mNKLs3F53PaRC-zJY",
  method: "encrypt"
};
const CF_DECRYPT = { ...CF_ENCRYPT, method: "decrypt", data: {
  "0": "pZcMycqqj8+xRxoVyJApweHJLMVYfn7tubOYiZTnmi8=\n",
  "1": "zZ8IL5Z6JFLXuZCDL22Pb3QGv6Js/hWwQopRNZONitU=\n",
  "2": "v7RssnlnxdhPMstakdt0ILbP+83KGQ4JmoDcMDpqrQA=\n",
  "3": "PeTom/lDUfHbcAe86E1sPxNlrImh48NDunEEN4LOIrE=\n",
  "4": "Y7Hss5aQBOYeIbcwihsjoP19kBCP5pBYYp1olTyPr54=\n",
  "5": "HlIRC5oPDFPKjqICWlaDB7fySWY+Ao4xvG5FVnlMVYGcROeSfmforcJpZHzrampQ\n",
  "6": "PjGB8TsEqgQE7bbEiGcm0jyNtK7SITk1Nlv1pqhOvi1Q+Ltz3Ddh5mBMWLUaOmyh\n",
  "7": "guCma9/oIQjHhuY3dfi2Pg1sVFYENpNrfld4Hrd6Y/M=\n",
  "8": "MNVX+1pa87pCPQcLtU73Ia3wuvGrfYeEOPEVhV5CXCWQ+Nrittiggh9sokAwrmSB\n",
  "9": "P/VW8l0SVRMIefmwnBlPUFe3QDyu+87lp+FL7NmxtBjR2GOxq5C2jUW/8/EDo63z\n"
}};

const CRYPTO_ENC_DATA = { data: {
  "0": "This is a test string.", "1": "Confidential data 12345.",
  "2": "Password: P@ssw0rd!", "3": "User's email: user@example.com",
  "4": "Sensitive transaction details.", "5": "Personal Identification Number: 9876",
  "6": "Credit Card Number: 4111 1111 1111 1111", "7": "API Key: AKIAIOSFODNN7EXAMPLE",
  "8": "Token: 9d8f7e6b-4c3a-2f1e-b5c7-8a9b1c2d3e4f",
  "9": "Location Coordinates: 37.7749\u00b0 N, 122.4194\u00b0 W"
}};
const CRYPTO_DEC_DATA = { data: {
  "0": "3HPZ4UPTX5ifxr5/GmTRPemTXQtssYIxErFXgxqSy7c=\n",
  "1": "gP5kc/tCd0iKjDl0VMGgM56W2ObVScGGyKcwsvQWEwY=\n",
  "2": "68O/0ETwvqiqncHePgaCGXxKGy2/84JDAQMmKhTKCtY=\n",
  "3": "kAPGu3b9hI7YvxnQbVUv1iTFzSEAviL6Y66gPb6oJ7M=\n",
  "4": "pU88zAXyy2q9euwUJGzC3K+vHCtVK3sDT/aN+zRU27w=\n",
  "5": "Ntxa8MChLP0cXNxtx/UEbZGNoK/q6cNQ72DMOaDXnlUV+TcKW0GG9Rgu8+taYP5O\n",
  "6": "LSHeG73BZyN9KFxYsBQTQ5LhG+zt9XIBtmNhKnMytnzEP4jQXEt0UsLVzfjBAj1r\n",
  "7": "zrSEXiaistx5LDzfaQe3WBuofNrntAyCYgOD92AzR7g=\n",
  "8": "BxypK8MM6y1v3+1C24ndj++XVm5nKgqhz++hwBX/Vz6h+KvLsNnaHx0neSAqoglI\n",
  "9": "7OYBQEDt+3EwKVnxEulLbeaGP2b22d2atMQg1SBlqQeqjmQ4DgaxnpCfMEDigLHM\n"
}};

// ─── Sample Code ───

const CF_SAMPLE = {
  Python: `import requests

url = 'https://demo-cf.safedb.biz/safedb_service/v1/cf_service/json'
data = {
    "data": {"0": "Hello World"},
    "uuid": "YOUR_UUID",
    "apikey": "YOUR_APIKEY",
    "method": "encrypt"
}
response = requests.post(url, json=data)
print(response.json())`,
  Java: `URL url = new URL("https://demo-cf.safedb.biz/safedb_service/v1/cf_service/json");
HttpURLConnection con = (HttpURLConnection) url.openConnection();
con.setRequestMethod("POST");
con.setRequestProperty("Content-Type", "application/json; utf-8");
con.setDoOutput(true);

String json = "{\\"data\\":{\\"0\\":\\"Hello\\"},\\"uuid\\":\\"..\\",\\"apikey\\":\\"..\\",\\"method\\":\\"encrypt\\"}";
try (OutputStream os = con.getOutputStream()) {
    os.write(json.getBytes("utf-8"));
}`,
  'Node.js': `const fetch = require('node-fetch');

fetch('https://demo-cf.safedb.biz/safedb_service/v1/cf_service/json', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
        data: { "0": "Hello World" },
        uuid: "YOUR_UUID", apikey: "YOUR_APIKEY", method: "encrypt"
    })
}).then(r => r.json()).then(console.log);`
};

const CRYPTO_SAMPLE = {
  curl: `curl -X POST 'https://crypto.safedb.biz/encrypt' \\
  -H 'Apikey: YOUR_APIKEY' \\
  -H 'Policy-UUID: YOUR_UUID' \\
  -H 'Content-Type: application/json' \\
  -d '{"data": {"0": "Hello World"}}'`,
  Python: `import requests

headers = {
    "Apikey": "YOUR_APIKEY",
    "Policy-UUID": "YOUR_UUID",
    "Content-Type": "application/json"
}
res = requests.post("https://crypto.safedb.biz/encrypt",
                     headers=headers, json={"data": {"0": "Hello"}})
print(res.json())`,
  'Node.js': `fetch('https://crypto.safedb.biz/encrypt', {
    method: 'POST',
    headers: {
        'Apikey': 'YOUR_APIKEY',
        'Policy-UUID': 'YOUR_UUID',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({ data: { "0": "Hello" } })
}).then(r => r.json()).then(console.log);`
};

// ─── Utils ───

const tryFormat = (s) => { try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; } };
const byteSize = (s) => new Blob([s]).size;
const fmtBytes = (b) => b < 1024 ? `${b} B` : `${(b / 1024).toFixed(1)} KB`;

// ═══════════════════════════════════════
// CF Panel
// ═══════════════════════════════════════

const CfPanel = () => {
  const [body, setBody] = useState(JSON.stringify(CF_ENCRYPT, null, 2));
  const [dbSchema, setDbSchema] = useState('safedb_scm');

  useEffect(() => {
    const config = getConfig();
    fetch(`${config.API_URL}/config/db-schema`)
      .then(res => res.json())
      .then(data => { if (data.dbSchema) setDbSchema(data.dbSchema); })
      .catch(() => {});
  }, []);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resTime, setResTime] = useState(null);
  const [status, setStatus] = useState(null);
  const [copiedBody, copyBody] = useCopy();
  const [copiedRes, copyRes] = useCopy();
  const [copiedCurl, copyCurl] = useCopy();
  const [sampleLang, setSampleLang] = useState('Python');

  const realUrl = 'https://demo-cf.safedb.biz/safedb_service/v1/cf_service/json';
  const proxyUrl = '/proxy-cf/safedb_service/v1/cf_service/json';

  const curlCmd = `curl -X POST '${realUrl}' \\\n  -H 'Content-Type: application/json' \\\n  -H 'X-Db-Schema: ${dbSchema}' \\\n  -d '${body.replace(/'/g, "'\\''")}'`;

  const send = useCallback(async () => {
    setLoading(true); setResponse(null); setStatus(null);
    const t0 = performance.now();
    try {
      const res = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Db-Schema': dbSchema },
        body
      });
      setStatus(res.status);
      setResponse(JSON.stringify(await res.json(), null, 2));
    } catch (err) {
      setStatus('Error');
      setResponse(JSON.stringify({ error: err.message }, null, 2));
    }
    setResTime(Math.round(performance.now() - t0));
    setLoading(false);
  }, [body, dbSchema]);

  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send(); }
    if (e.key === 'Tab') {
      e.preventDefault();
      const { selectionStart: s, selectionEnd: end } = e.target;
      const v = body.substring(0, s) + '  ' + body.substring(end);
      setBody(v);
      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = s + 2; }, 0);
    }
  };

  return (
    <Row>
      <Col lg={7} className="mb-4">
        {/* URL Bar */}
        <UrlBarWrapper className="mb-3">
          <MethodBadge>POST</MethodBadge>
          <Form.Control value={realUrl} readOnly
            style={{ border: 'none', boxShadow: 'none', fontSize: '.85rem', color: '#4a5568', background: 'transparent' }} />
          <Button variant="primary" onClick={send} disabled={loading}
            style={{ minWidth: 90, fontWeight: 600, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            {loading ? <><Spinner size="sm" animation="border" /><LoadingDots /></>
              : <><FontAwesomeIcon icon={faPaperPlane} size="sm" /> 전송</>}
          </Button>
        </UrlBarWrapper>

        {/* Headers */}
        <div className="mt-3">
          <SectionLabel>
            <Badge bg="info" style={{ fontSize: '.7rem' }}>HEADERS</Badge>
            Request Headers
          </SectionLabel>
          <div className="p-3" style={{ background: '#f7fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <HeaderRow>
              <Form.Control size="sm" value="Content-Type" readOnly style={{ maxWidth: 130, background: '#edf2f7', fontWeight: 600, fontSize: '.82rem' }} />
              <Form.Control size="sm" value="application/json" readOnly style={{ background: '#edf2f7', fontSize: '.82rem' }} />
            </HeaderRow>
            <HeaderRow>
              <Form.Control size="sm" value="X-Db-Schema" readOnly style={{ maxWidth: 130, background: '#edf2f7', fontWeight: 600, fontSize: '.82rem' }} />
              <Form.Control size="sm" value={dbSchema} onChange={e => setDbSchema(e.target.value)} style={{ fontSize: '.82rem' }} />
              <OverlayTrigger placement="top" overlay={<Tooltip>Cloud Function이 조회할 DB 스키마명</Tooltip>}>
                <FontAwesomeIcon icon={faDatabase} size="sm" style={{ color: '#718096', cursor: 'help' }} />
              </OverlayTrigger>
            </HeaderRow>
          </div>
        </div>

        {/* curl command */}
        <CurlBox>
          <CopyBtn $copied={copiedCurl} onClick={() => copyCurl(curlCmd)}>
            <FontAwesomeIcon icon={copiedCurl ? faCheck : faCopy} size="xs" />
            {copiedCurl ? 'Copied!' : 'Copy'}
          </CopyBtn>
          <div className="d-flex align-items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faTerminal} size="sm" style={{ color: '#718096' }} />
            <span style={{ fontSize: '.78rem', fontWeight: 600, color: '#718096' }}>cURL</span>
          </div>
          <CurlPre>{curlCmd}</CurlPre>
        </CurlBox>

        {/* Request Body */}
        <div className="mt-4" style={{ position: 'relative' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <SectionLabel>
              <Badge bg="dark" style={{ fontSize: '.7rem' }}>BODY</Badge>
              Request Body
              <Badge bg="light" text="dark" style={{ fontSize: '.7rem' }}>{fmtBytes(byteSize(body))}</Badge>
            </SectionLabel>
            <div className="d-flex gap-2">
              <Button variant="outline-success" size="sm" style={{ fontSize: '.75rem', borderRadius: 6 }}
                onClick={() => setBody(JSON.stringify(CF_ENCRYPT, null, 2))}>
                <FontAwesomeIcon icon={faLock} size="xs" className="me-1" />암호화
              </Button>
              <Button variant="outline-info" size="sm" style={{ fontSize: '.75rem', borderRadius: 6 }}
                onClick={() => setBody(JSON.stringify(CF_DECRYPT, null, 2))}>
                <FontAwesomeIcon icon={faLockOpen} size="xs" className="me-1" />복호화
              </Button>
              <Button variant="outline-secondary" size="sm" style={{ fontSize: '.75rem', borderRadius: 6 }}
                onClick={() => setBody(tryFormat(body))}>Format</Button>
            </div>
          </div>
          <small className="text-muted d-block mb-2">Ctrl+Enter 전송 &middot; Tab 들여쓰기</small>
          <div style={{ position: 'relative' }}>
            <CodeArea value={body} onChange={e => setBody(e.target.value)} onKeyDown={onKey} spellCheck={false} />
            <CopyBtn $copied={copiedBody} onClick={() => copyBody(body)} style={{ top: 10, right: 10 }}>
              <FontAwesomeIcon icon={copiedBody ? faCheck : faCopy} size="xs" />
              {copiedBody ? 'Copied!' : 'Copy'}
            </CopyBtn>
          </div>
        </div>

        {/* Response */}
        <div className="mt-4">
          <SectionLabel>
            <Badge bg="success" style={{ fontSize: '.7rem' }}>RESPONSE</Badge>
            Response
            {response && <Badge bg="light" text="dark" style={{ fontSize: '.7rem' }}>{fmtBytes(byteSize(response))}</Badge>}
          </SectionLabel>
          {status !== null && (
            <StatusBar $error={status !== 200}>
              <span><strong>Status </strong>
                <Badge bg={status === 200 ? 'success' : 'danger'}>{status === 200 ? '200 OK' : status}</Badge>
              </span>
              <span><strong>Time </strong>{resTime}ms</span>
              {response && <span><strong>Size </strong>{fmtBytes(byteSize(response))}</span>}
            </StatusBar>
          )}
          {response ? (
            <div style={{ position: 'relative' }}>
              <ResponsePre $error={status !== 200}>{response}</ResponsePre>
              <CopyBtn $copied={copiedRes} onClick={() => copyRes(response)} style={{ top: 10, right: 10 }}>
                <FontAwesomeIcon icon={copiedRes ? faCheck : faCopy} size="xs" />
                {copiedRes ? 'Copied!' : 'Copy'}
              </CopyBtn>
            </div>
          ) : (
            <EmptyResponse>
              <FontAwesomeIcon icon={faPaperPlane} size="2x" style={{ color: '#cbd5e0', marginBottom: 12 }} />
              <p className="mb-1 fw-semibold">응답 대기 중</p>
              <small>전송 버튼을 클릭하거나 Ctrl+Enter를 누르세요</small>
            </EmptyResponse>
          )}
        </div>
      </Col>

      {/* Documentation */}
      <Col lg={5}>
        <DocScroll>
          <div className="d-flex align-items-center gap-2 border-bottom pb-2 mb-3">
            <FontAwesomeIcon icon={faBook} style={{ color: '#ff6c37' }} />
            <h6 className="fw-bold mb-0">Cloud Functions API 문서</h6>
          </div>
          <div className="p-2 mb-3" style={{ background: '#fff7ed', borderRadius: 8, border: '1px solid #fed7aa', fontSize: '.82rem' }}>
            <FontAwesomeIcon icon={faCloud} className="me-1" style={{ color: '#f97316' }} />
            클라우드 펑션을 사용한 표준 알고리즘 암호화/복호화. <strong>요청·응답 1MB 제한</strong>
          </div>

          <Accordion defaultActiveKey={['0']} alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>요청 파라미터</Accordion.Header>
              <Accordion.Body className="p-2">
                <Table size="sm" bordered hover className="mb-0" style={{ fontSize: '.8rem' }}>
                  <thead className="table-light"><tr><th>파라미터</th><th>타입</th><th>필수</th><th>설명</th></tr></thead>
                  <tbody>
                    <tr><td><code>data</code></td><td>JSON</td><td><Badge bg="danger" style={{ fontSize: '.65rem' }}>필수</Badge></td><td>암호화할 데이터 (key:value)</td></tr>
                    <tr><td><code>uuid</code></td><td>String</td><td><Badge bg="danger" style={{ fontSize: '.65rem' }}>필수</Badge></td><td>정책 아이디 (키, IV, 알고리즘, 모드, 패딩)</td></tr>
                    <tr><td><code>apikey</code></td><td>String</td><td><Badge bg="danger" style={{ fontSize: '.65rem' }}>필수</Badge></td><td>인증키</td></tr>
                    <tr><td><code>method</code></td><td>String</td><td><Badge bg="danger" style={{ fontSize: '.65rem' }}>필수</Badge></td><td><code>encrypt</code> 또는 <code>decrypt</code></td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>응답 형식</Accordion.Header>
              <Accordion.Body className="p-2">
                <Table size="sm" bordered hover className="mb-0" style={{ fontSize: '.8rem' }}>
                  <thead className="table-light"><tr><th>파라미터</th><th>타입</th><th>설명</th></tr></thead>
                  <tbody>
                    <tr><td><code>processedData</code></td><td>JSON</td><td>처리 결과 데이터</td></tr>
                    <tr><td><code>uuid</code></td><td>String</td><td>사용된 정책명</td></tr>
                    <tr><td><code>method</code></td><td>String</td><td>encrypt / decrypt</td></tr>
                    <tr><td><code>successCount</code></td><td>String</td><td>성공 건수</td></tr>
                    <tr><td><code>failureCount</code></td><td>String</td><td>실패 건수</td></tr>
                    <tr><td><code>errorList</code></td><td>JSON</td><td>실패 항목과 원인</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>에러 처리</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2"><Badge bg="secondary" style={{ fontSize: '.68rem' }}>잘못된 UUID</Badge></div>
                <CodeBlock>{`{"error_msg": "UUID not found: 966686a4-..."}`}</CodeBlock>
                <div className="mb-2 mt-3"><Badge bg="secondary" style={{ fontSize: '.68rem' }}>잘못된 인증키</Badge></div>
                <CodeBlock>{`{"error_msg": "Apikey not found or invalid: ..."}`}</CodeBlock>
                <div className="mb-2 mt-3"><Badge bg="secondary" style={{ fontSize: '.68rem' }}>권한 미설정</Badge></div>
                <CodeBlock>{`{"error_msg": "Permission to encrypt is denied"}\n{"error_msg": "Permission to decrypt is denied"}`}</CodeBlock>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>샘플 코드</Accordion.Header>
              <Accordion.Body className="p-2">
                <Nav variant="pills" className="mb-2">
                  {Object.keys(CF_SAMPLE).map(l => (
                    <Nav.Item key={l}><Nav.Link active={sampleLang === l} onClick={() => setSampleLang(l)}
                      style={{ fontSize: '.76rem', padding: '3px 10px', cursor: 'pointer' }}>{l}</Nav.Link></Nav.Item>
                  ))}
                </Nav>
                <CodeBlock>{CF_SAMPLE[sampleLang]}</CodeBlock>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </DocScroll>
      </Col>
    </Row>
  );
};

// ═══════════════════════════════════════
// Crypto Panel
// ═══════════════════════════════════════

const CryptoPanel = () => {
  const [operation, setOperation] = useState('encrypt');
  const [apikey, setApikey] = useState('9x7tgiocEVxnrC7JndBeSDvambrqDZ91vtqogA0DTsA');
  const [policyUuid, setPolicyUuid] = useState('c4b0bda3-2759-42e5-c5d7-g73d326fe49c');
  const [body, setBody] = useState(JSON.stringify(CRYPTO_ENC_DATA, null, 2));
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resTime, setResTime] = useState(null);
  const [status, setStatus] = useState(null);
  const [copiedBody, copyBody] = useCopy();
  const [copiedRes, copyRes] = useCopy();
  const [copiedCurl, copyCurl] = useCopy();
  const [sampleLang, setSampleLang] = useState('curl');

  const realUrl = `https://crypto.safedb.biz/${operation}`;
  const proxyUrl = `/proxy-crypto/${operation}`;

  const curlCmd = `curl -X POST '${realUrl}' \\\n  -H 'Apikey: ${apikey}' \\\n  -H 'Policy-UUID: ${policyUuid}' \\\n  -H 'Content-Type: application/json' \\\n  -d '${body.replace(/'/g, "'\\''")}'`;

  const send = useCallback(async () => {
    setLoading(true); setResponse(null); setStatus(null);
    const t0 = performance.now();
    try {
      const res = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Apikey': apikey, 'Policy-UUID': policyUuid },
        body
      });
      setStatus(res.status);
      setResponse(JSON.stringify(await res.json(), null, 2));
    } catch (err) {
      setStatus('Error');
      setResponse(JSON.stringify({ error: err.message }, null, 2));
    }
    setResTime(Math.round(performance.now() - t0));
    setLoading(false);
  }, [operation, apikey, policyUuid, body]);

  const loadExample = (type) => {
    setOperation(type);
    setBody(JSON.stringify(type === 'encrypt' ? CRYPTO_ENC_DATA : CRYPTO_DEC_DATA, null, 2));
  };

  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send(); }
    if (e.key === 'Tab') {
      e.preventDefault();
      const { selectionStart: s, selectionEnd: end } = e.target;
      const v = body.substring(0, s) + '  ' + body.substring(end);
      setBody(v);
      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = s + 2; }, 0);
    }
  };

  return (
    <Row>
      <Col lg={7} className="mb-4">
        {/* URL Bar */}
        <UrlBarWrapper className="mb-3">
          <MethodBadge>POST</MethodBadge>
          <Form.Select value={operation} onChange={e => setOperation(e.target.value)}
            style={{ border: 'none', boxShadow: 'none', maxWidth: 130, fontSize: '.85rem', fontWeight: 600 }}>
            <option value="encrypt">/encrypt</option>
            <option value="decrypt">/decrypt</option>
          </Form.Select>
          <Form.Control value="https://crypto.safedb.biz" readOnly
            style={{ border: 'none', boxShadow: 'none', fontSize: '.85rem', color: '#4a5568', background: 'transparent' }} />
          <Button variant="primary" onClick={send} disabled={loading}
            style={{ minWidth: 90, fontWeight: 600, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            {loading ? <><Spinner size="sm" animation="border" /><LoadingDots /></>
              : <><FontAwesomeIcon icon={faPaperPlane} size="sm" /> 전송</>}
          </Button>
        </UrlBarWrapper>

        {/* curl */}
        <CurlBox>
          <CopyBtn $copied={copiedCurl} onClick={() => copyCurl(curlCmd)}>
            <FontAwesomeIcon icon={copiedCurl ? faCheck : faCopy} size="xs" />
            {copiedCurl ? 'Copied!' : 'Copy'}
          </CopyBtn>
          <div className="d-flex align-items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faTerminal} size="sm" style={{ color: '#718096' }} />
            <span style={{ fontSize: '.78rem', fontWeight: 600, color: '#718096' }}>cURL</span>
          </div>
          <CurlPre>{curlCmd}</CurlPre>
        </CurlBox>

        {/* Headers */}
        <div className="mt-4">
          <SectionLabel>
            <Badge bg="info" style={{ fontSize: '.7rem' }}>HEADERS</Badge>
            Request Headers
          </SectionLabel>
          <div className="p-3" style={{ background: '#f7fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <HeaderRow>
              <Form.Control size="sm" value="Apikey" readOnly style={{ maxWidth: 120, background: '#edf2f7', fontWeight: 600, fontSize: '.82rem' }} />
              <Form.Control size="sm" value={apikey} onChange={e => setApikey(e.target.value)} style={{ fontSize: '.82rem' }} />
            </HeaderRow>
            <HeaderRow>
              <Form.Control size="sm" value="Policy-UUID" readOnly style={{ maxWidth: 120, background: '#edf2f7', fontWeight: 600, fontSize: '.82rem' }} />
              <Form.Control size="sm" value={policyUuid} onChange={e => setPolicyUuid(e.target.value)} style={{ fontSize: '.82rem' }} />
            </HeaderRow>
            <HeaderRow>
              <Form.Control size="sm" value="Content-Type" readOnly style={{ maxWidth: 120, background: '#edf2f7', fontWeight: 600, fontSize: '.82rem' }} />
              <Form.Control size="sm" value="application/json" readOnly style={{ background: '#edf2f7', fontSize: '.82rem' }} />
            </HeaderRow>
          </div>
        </div>

        {/* Body */}
        <div className="mt-4" style={{ position: 'relative' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <SectionLabel>
              <Badge bg="dark" style={{ fontSize: '.7rem' }}>BODY</Badge>
              Request Body
              <Badge bg="light" text="dark" style={{ fontSize: '.7rem' }}>{fmtBytes(byteSize(body))}</Badge>
            </SectionLabel>
            <div className="d-flex gap-2">
              <Button variant="outline-success" size="sm" style={{ fontSize: '.75rem', borderRadius: 6 }}
                onClick={() => loadExample('encrypt')}>
                <FontAwesomeIcon icon={faLock} size="xs" className="me-1" />암호화
              </Button>
              <Button variant="outline-info" size="sm" style={{ fontSize: '.75rem', borderRadius: 6 }}
                onClick={() => loadExample('decrypt')}>
                <FontAwesomeIcon icon={faLockOpen} size="xs" className="me-1" />복호화
              </Button>
              <Button variant="outline-secondary" size="sm" style={{ fontSize: '.75rem', borderRadius: 6 }}
                onClick={() => setBody(tryFormat(body))}>Format</Button>
            </div>
          </div>
          <small className="text-muted d-block mb-2">Ctrl+Enter 전송 &middot; Tab 들여쓰기</small>
          <div style={{ position: 'relative' }}>
            <CodeArea value={body} onChange={e => setBody(e.target.value)} onKeyDown={onKey} spellCheck={false} $h="220px" />
            <CopyBtn $copied={copiedBody} onClick={() => copyBody(body)} style={{ top: 10, right: 10 }}>
              <FontAwesomeIcon icon={copiedBody ? faCheck : faCopy} size="xs" />
              {copiedBody ? 'Copied!' : 'Copy'}
            </CopyBtn>
          </div>
        </div>

        {/* Response */}
        <div className="mt-4">
          <SectionLabel>
            <Badge bg="success" style={{ fontSize: '.7rem' }}>RESPONSE</Badge>
            Response
            {response && <Badge bg="light" text="dark" style={{ fontSize: '.7rem' }}>{fmtBytes(byteSize(response))}</Badge>}
          </SectionLabel>
          {status !== null && (
            <StatusBar $error={status !== 200}>
              <span><strong>Status </strong>
                <Badge bg={status === 200 ? 'success' : 'danger'}>{status === 200 ? '200 OK' : status}</Badge>
              </span>
              <span><strong>Time </strong>{resTime}ms</span>
              {response && <span><strong>Size </strong>{fmtBytes(byteSize(response))}</span>}
            </StatusBar>
          )}
          {response ? (
            <div style={{ position: 'relative' }}>
              <ResponsePre $error={status !== 200}>{response}</ResponsePre>
              <CopyBtn $copied={copiedRes} onClick={() => copyRes(response)} style={{ top: 10, right: 10 }}>
                <FontAwesomeIcon icon={copiedRes ? faCheck : faCopy} size="xs" />
                {copiedRes ? 'Copied!' : 'Copy'}
              </CopyBtn>
            </div>
          ) : (
            <EmptyResponse>
              <FontAwesomeIcon icon={faPaperPlane} size="2x" style={{ color: '#cbd5e0', marginBottom: 12 }} />
              <p className="mb-1 fw-semibold">응답 대기 중</p>
              <small>전송 버튼을 클릭하거나 Ctrl+Enter를 누르세요</small>
            </EmptyResponse>
          )}
        </div>
      </Col>

      {/* Documentation */}
      <Col lg={5}>
        <DocScroll>
          <div className="d-flex align-items-center gap-2 border-bottom pb-2 mb-3">
            <FontAwesomeIcon icon={faBook} style={{ color: '#ff6c37' }} />
            <h6 className="fw-bold mb-0">Crypto API 문서</h6>
          </div>
          <div className="p-2 mb-3" style={{ background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe', fontSize: '.82rem' }}>
            <FontAwesomeIcon icon={faServer} className="me-1" style={{ color: '#3b82f6' }} />
            Server 인스턴스 Crypto 모듈. <strong>요청 데이터 10MB 제한</strong>
          </div>

          <Accordion defaultActiveKey={['0']} alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>기본 정보 &amp; 헤더</Accordion.Header>
              <Accordion.Body className="p-2">
                <Table size="sm" bordered hover className="mb-2" style={{ fontSize: '.8rem' }}>
                  <thead className="table-light"><tr><th>기능</th><th>메서드</th><th>URL</th></tr></thead>
                  <tbody>
                    <tr><td>암호화</td><td><Badge bg="success" style={{ fontSize: '.68rem' }}>POST</Badge></td><td><code>/encrypt</code></td></tr>
                    <tr><td>복호화</td><td><Badge bg="success" style={{ fontSize: '.68rem' }}>POST</Badge></td><td><code>/decrypt</code></td></tr>
                  </tbody>
                </Table>
                <Table size="sm" bordered hover className="mb-0" style={{ fontSize: '.8rem' }}>
                  <thead className="table-light"><tr><th>헤더</th><th>타입</th><th>필수</th><th>설명</th></tr></thead>
                  <tbody>
                    <tr><td><code>Apikey</code></td><td>String</td><td><Badge bg="danger" style={{ fontSize: '.65rem' }}>필수</Badge></td><td>서비스 이용 apikey</td></tr>
                    <tr><td><code>Policy-UUID</code></td><td>String</td><td><Badge bg="danger" style={{ fontSize: '.65rem' }}>필수</Badge></td><td>정책 조회 UUID</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>요청 본문</Accordion.Header>
              <Accordion.Body className="p-2">
                <Table size="sm" bordered hover className="mb-0" style={{ fontSize: '.8rem' }}>
                  <thead className="table-light"><tr><th>파라미터</th><th>타입</th><th>필수</th><th>설명</th></tr></thead>
                  <tbody>
                    <tr><td><code>data</code></td><td>Map&lt;String,String&gt;</td><td><Badge bg="danger" style={{ fontSize: '.65rem' }}>필수</Badge></td><td>암·복호화 대상 데이터 (key:value)</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>응답 형식</Accordion.Header>
              <Accordion.Body className="p-2">
                <Table size="sm" bordered hover className="mb-0" style={{ fontSize: '.8rem' }}>
                  <thead className="table-light"><tr><th>파라미터</th><th>타입</th><th>설명</th></tr></thead>
                  <tbody>
                    <tr><td><code>processedData</code></td><td>Map</td><td>처리 결과</td></tr>
                    <tr><td><code>uuid</code></td><td>String</td><td>Policy-UUID</td></tr>
                    <tr><td><code>method</code></td><td>String</td><td>encrypt / decrypt</td></tr>
                    <tr><td><code>successCount</code></td><td>Integer</td><td>성공 수</td></tr>
                    <tr><td><code>failureCount</code></td><td>Integer</td><td>실패 수</td></tr>
                    <tr><td><code>errorList</code></td><td>Map</td><td>에러 목록</td></tr>
                    <tr><td><code>code</code></td><td>Integer</td><td>상태 코드 (오류 시)</td></tr>
                    <tr><td><code>message</code></td><td>String</td><td>메시지 (오류 시)</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>샘플 코드</Accordion.Header>
              <Accordion.Body className="p-2">
                <Nav variant="pills" className="mb-2">
                  {Object.keys(CRYPTO_SAMPLE).map(l => (
                    <Nav.Item key={l}><Nav.Link active={sampleLang === l} onClick={() => setSampleLang(l)}
                      style={{ fontSize: '.76rem', padding: '3px 10px', cursor: 'pointer' }}>{l}</Nav.Link></Nav.Item>
                  ))}
                </Nav>
                <CodeBlock>{CRYPTO_SAMPLE[sampleLang]}</CodeBlock>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </DocScroll>
      </Col>
    </Row>
  );
};

// ═══════════════════════════════════════
// Main Page
// ═══════════════════════════════════════

const ApiTest = () => {
  const [activeApi, setActiveApi] = useState('cf');

  return (
    <PageWrapper className="container-fluid mt-4 px-4">
      <Card className="shadow-sm border-0" style={{ borderRadius: 12, overflow: 'hidden' }}>
        <Card.Header className="d-flex align-items-center justify-content-between py-3 px-4"
          style={{ background: 'linear-gradient(135deg, #ff6c37, #ff8c37)', border: 'none' }}>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faTerminal} style={{ color: 'rgba(255,255,255,.8)' }} />
            <h5 className="mb-0 fw-bold text-white">API 테스트</h5>
            <Badge bg="light" text="dark" style={{ fontSize: '.68rem', fontWeight: 500 }}>Demo</Badge>
          </div>
          <div className="d-flex gap-2">
            <TabButton $active={activeApi === 'cf'} onClick={() => setActiveApi('cf')}>
              <FontAwesomeIcon icon={faCloud} size="sm" className="me-1" />
              Cloud Functions
            </TabButton>
            <TabButton $active={activeApi === 'crypto'} onClick={() => setActiveApi('crypto')}>
              <FontAwesomeIcon icon={faServer} size="sm" className="me-1" />
              Crypto API
            </TabButton>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          {activeApi === 'cf' ? <CfPanel /> : <CryptoPanel />}
        </Card.Body>
      </Card>
    </PageWrapper>
  );
};

export default ApiTest;
